import { DOCUMENT, Injectable, InjectionToken, PLATFORM_ID, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { RoutesService } from '../../../routes.service';
import { PreferencesService } from '../../../preferences.service';
import { GuiaPrefsEmpresa } from '../preferences-base.service';
import { UnifiedAuthService } from '../unified-auth.service';
import { AiAgentService } from '../API/ai-agent.service';
import { SubPerfilEnum } from '../../enum/default-profiles.enum';
import { TemRota, capituloValePelaVisita } from './tour-elegibilidade';
import { TrechoTour, trechoDaTela, trechoDoCapitulo } from './tour-mapa-telas';
import { capitulosConcluidosAte } from './tour-progresso';
import { rotaEhHome, rotaPermiteTour } from './tour-rota-permitida';
import { ROTEIRO_JORNADA } from './tour.roteiro';
import { RoteiroTour } from './tour.types';
import { TourRoteiroService } from './tour-roteiro.service';

/** D67 — as 4 ações que o orbe pode oferecer, nesta ordem quando presentes. */
export type IdAcaoGuia = 'tour-tela' | 'continuar' | 'comecar' | 'refazer-capitulo';

export interface AcaoGuia {
  id: IdAcaoGuia;
  rotulo: string;
  icone: string;
}

/** H9 (2026-11-05) — item do menu do usuário: "Tour" enquanto a jornada não foi concluída,
 *  "Tour desta página" depois. `desabilitada` + `dica` cobrem a página sem trecho de tour. */
export interface AcaoMenuGuia {
  id: 'comecar' | 'continuar' | 'tour-tela';
  rotulo: string;
  desabilitada: boolean;
  dica: string | null;
}

/**
 * Decisão do usuário (2026-11-05): "por enquanto" o One não oferece o tour a ninguém — nem o
 * convite no orbe nem o cartão da Home, inclusive para usuário novo. O tour fica a um clique no
 * menu do usuário (`acaoDoMenu`). Com `false`, `conviteSilenciado()` é sempre verdadeiro.
 *
 * Religar é trocar a fábrica para `true`: as regras de quando a oferta voltar já estão prontas e
 * testadas — convite só na Home, UMA recusa silencia a empresa para sempre (blob de preferências),
 * cartão da Home com X.
 */
export const OFERTA_TOUR_LIGADA = new InjectionToken<boolean>('OFERTA_TOUR_LIGADA', {
  providedIn: 'root',
  factory: () => false,
});

/** D74 — insumo do cartão "próximo passo" da Home (Task 6). */
export interface ProgressoJornada {
  estado: 'novato' | 'pendente' | 'concluido' | 'inelegivel';
  capitulosConcluidos: number;
  totalCapitulos: number;
  capituloPendente: string | null;
  /** O convite não deve ser feito: a oferta está desligada (`OFERTA_TOUR_LIGADA`) ou o usuário
   *  já a recusou nesta empresa — o cartão da Home se esconde junto, em qualquer estado. */
  conviteSilenciado: boolean;
}

/** H7 (2026-11-05): UMA recusa na empresa silencia o convite para sempre ("recusou, some de
 *  vez"). Era 2 (B2, 2026-10-13); quem já tinha 1 recusa gravada passa a estar silenciado. */
export const MAX_RECUSAS_CONVITE = 1;

const GUIA_VAZIO: GuiaPrefsEmpresa = { convitesRecusados: 0, telasDispensadas: [] };

/**
 * D67/D69/D70 — dono único da lógica do "Guia One": que ações oferecer no orbe (chips), o que
 * fazer quando o usuário clica nelas, o convite de tour (migrado do `app.component`, D70) e o
 * atalho de teclado `?` (D69). Componentes (Task 4) só renderizam o que este serviço decide.
 *
 * `providedIn: 'root'` + injeção cedo no `app.component` (comentário lá) é o que garante que o
 * primeiro `NavigationEnd` autenticado já encontra o serviço vivo para decidir a oferta — o
 * mesmo motivo por que `avaliarOferta` existia no `app.component` antes desta migração.
 */
@Injectable({ providedIn: 'root' })
export class GuiaOneService {
  private readonly router = inject(Router);
  private readonly routes = inject(RoutesService);
  private readonly auth = inject(UnifiedAuthService);
  private readonly motor = inject(TourRoteiroService);
  private readonly ai = inject(AiAgentService);
  private readonly prefs = inject(PreferencesService);
  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly ofertaLigada = inject(OFERTA_TOUR_LIGADA);

  /** Mesmo roteiro do motor — o mapa de telas (D65) precisa dele para achar o trecho da tela atual. */
  private readonly roteiro: RoteiroTour = ROTEIRO_JORNADA;
  /** Idêntico ao `temRota` privado do motor (`tour-roteiro.service.ts`) — a mesma fonte de
   *  "o usuário tem a tela?", nunca uma segunda noção de permissão. */
  private readonly temRota: TemRota = (sp) => this.auth.GetSubPerfilRoute(sp) != null;
  /** Capturado uma vez: hostname não muda em runtime, só a URL — computeds só precisam
   *  reavaliar em `urlAtual()`. SSR-safe: `defaultView` é `null` no servidor. */
  private readonly hostname = this.doc.defaultView?.location?.hostname ?? '';

  private readonly _urlAtual = signal(this.router.url);
  readonly urlAtual: Signal<string> = this._urlAtual.asReadonly();

  private readonly _subPerfilAtual = signal<SubPerfilEnum | null>(this.routes.getActiveSubPerfil());
  readonly subPerfilAtual: Signal<SubPerfilEnum | null> = this._subPerfilAtual.asReadonly();

  readonly rotaPermitida: Signal<boolean> = computed(() =>
    rotaPermiteTour(this.urlAtual(), this.hostname));

  /** H1 (2026-11-05): a Home é a única tela onde o assistente fala por conta própria. */
  readonly naHome: Signal<boolean> = computed(() => rotaEhHome(this.urlAtual()));

  /** D65 — trecho do roteiro que pertence à tela atual; `null` fora de qualquer subperfil
   *  mapeado (ex.: Home) ou quando o roteiro não tem passo nenhum para este subperfil. */
  readonly trechoAtual: Signal<TrechoTour | null> = computed(() => {
    const sp = this.subPerfilAtual();
    return sp === null ? null : trechoDaTela(this.roteiro, sp, this.temRota);
  });

  /**
   * D67 — chips do orbe, NESTA ORDEM, só com rota permitida e o tour ainda não ligado (o
   * orbe some as ações assim que o tour de fato começa — não faz sentido oferecer "começar"
   * de novo com o balão do roteiro já na tela):
   * 1. `tour-tela` — quando há trecho para a tela atual.
   * 2. `continuar` (tour pendente) OU `comecar` (elegível, nunca concluiu, nada pendente) —
   *    nunca os dois ao mesmo tempo.
   * 3. `refazer-capitulo` — só quando há trecho E o usuário já concluiu a jornada.
   */
  readonly acoes: Signal<AcaoGuia[]> = computed(() => {
    if (!this.rotaPermitida() || this.motor.ativo()) return [];
    const lista: AcaoGuia[] = [];
    const trecho = this.trechoAtual();
    if (trecho) lista.push({ id: 'tour-tela', rotulo: 'Tour desta tela', icone: 'travel_explore' });
    if (this.motor.temTourPendente()) {
      const capitulo = this.motor.capituloDoTourPendente();
      lista.push({
        id: 'continuar',
        rotulo: capitulo ? `Continuar o tour: ${capitulo}` : 'Continuar o tour',
        icone: 'play_arrow',
      });
    } else if (this.motor.elegivel() && !this.motor.jaConcluiu()) {
      lista.push({ id: 'comecar', rotulo: 'Começar o tour', icone: 'rocket_launch' });
    }
    if (trecho && this.motor.jaConcluiu()) {
      lista.push({ id: 'refazer-capitulo', rotulo: `Refazer: ${trecho.capitulo.nome}`, icone: 'replay' });
    }
    return lista;
  });

  /**
   * H9 — item do menu do usuário (canto superior direito). É a entrada manual que compensa a
   * oferta desligada: não depende de `conviteSilenciado`. `null` = sem item (usuário sem
   * permissão para o tour, tour já na tela, ou rota sem tour — PDV/admin/login).
   */
  readonly acaoDoMenu: Signal<AcaoMenuGuia | null> = computed(() => {
    if (!this.rotaPermitida() || this.motor.ativo()) return null;
    switch (this.progresso().estado) {
      case 'inelegivel':
        return null;
      case 'novato':
        return { id: 'comecar', rotulo: 'Tour', desabilitada: false, dica: null };
      case 'pendente':
        return { id: 'continuar', rotulo: 'Tour', desabilitada: false, dica: null };
      case 'concluido': {
        const temTrecho = this.trechoAtual() !== null;
        return {
          id: 'tour-tela', rotulo: 'Tour desta página',
          desabilitada: !temTrecho, dica: temTrecho ? null : 'Esta página não tem tour',
        };
      }
    }
  });

  // --- D70: convite de tour (migrado do app.component) ---

  /** null = nenhuma oferta; 'iniciar' = novato elegível; 'retomar' = tour pendente. */
  private readonly _oferta = signal<'iniciar' | 'retomar' | null>(null);
  readonly oferta: Signal<'iniciar' | 'retomar' | null> = this._oferta.asReadonly();
  /** Nome do capítulo em que o tour pendente parou — ver `textoConvite`. */
  private readonly _capituloRetomada = signal<string | null>(null);
  readonly capituloRetomada: Signal<string | null> = this._capituloRetomada.asReadonly();

  /** Garante que a oferta é decidida uma única vez por sessão — nunca resurge depois que o
   *  usuário dispensa. (O `effect` D61 que reabria a trava ao SAIR do tour foi removido em
   *  2026-10-13: a reoferta imediata era a chatice que o usuário reclamou.) */
  private tourOfertaResolvida = false;

  /** D74 — progresso persistido para o cartão da Home (Task 6). `totalCapitulos` usa o MESMO
   *  critério de `capitulosConcluidosAte` (capítulos que valem a visita) — ver o comentário
   *  daquela função em tour-progresso.ts para o motivo de não ser "todo capítulo do roteiro". */
  readonly progresso: Signal<ProgressoJornada> = computed(() => {
    const totalCapitulos = this.roteiro.capitulos.filter((c) =>
      capituloValePelaVisita(this.roteiro, c.id, this.temRota),
    ).length;
    const conviteSilenciado = this.conviteSilenciado();
    if (!this.motor.elegivel()) {
      return { estado: 'inelegivel', capitulosConcluidos: 0, totalCapitulos, capituloPendente: null, conviteSilenciado };
    }
    if (this.motor.jaConcluiu()) {
      return { estado: 'concluido', capitulosConcluidos: totalCapitulos, totalCapitulos, capituloPendente: null, conviteSilenciado };
    }
    if (this.motor.temTourPendente()) {
      const indice = this.prefs.getTourState()?.indice ?? 0;
      return {
        estado: 'pendente',
        capitulosConcluidos: capitulosConcluidosAte(this.roteiro, indice, this.temRota),
        totalCapitulos,
        capituloPendente: this.motor.capituloDoTourPendente(),
        conviteSilenciado,
      };
    }
    return { estado: 'novato', capitulosConcluidos: 0, totalCapitulos, capituloPendente: null, conviteSilenciado };
  });

  /** Chave da memória do guia: a empresa selecionada no auth (storage do navegador — sobrevive
   *  ao F5); `getCompanyState()` é só o fallback (é volátil: nasce 0 a cada carga até o menu setar). */
  idEmpresaDoGuia(): number {
    const doAuth = this.auth.getIdEmpresaSelecionada();
    if (doAuth) return doAuth;
    const doToken = (this.auth.getUserInfos() as unknown as { idEmpresa?: number } | null)?.idEmpresa;
    return doToken || this.prefs.getCompanyState() || 0;
  }

  /** Memória do guia da empresa atual (B1); vazia quando nunca gravada. Lê o signal do blob —
   *  `acoes`/`progresso` recomputam quando a memória muda. */
  guiaDaEmpresa(): GuiaPrefsEmpresa {
    return this.prefs.getGuiaState(this.idEmpresaDoGuia()) ?? GUIA_VAZIO;
  }

  /** Única porta de escrita da memória do guia (GuiaProatividadeService também grava por aqui). */
  atualizarGuiaDaEmpresa(mudar: (atual: GuiaPrefsEmpresa) => GuiaPrefsEmpresa): void {
    this.prefs.setGuiaState(this.idEmpresaDoGuia(), mudar(this.guiaDaEmpresa()));
    void this.prefs.persistirGuiaAgora();
  }

  /** Oferta desligada para todos (`OFERTA_TOUR_LIGADA`) OU recusada nesta empresa. */
  conviteSilenciado(): boolean {
    return !this.ofertaLigada || this.guiaDaEmpresa().convitesRecusados >= MAX_RECUSAS_CONVITE;
  }

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.aoNavegar());
  }

  private aoNavegar(): void {
    this._urlAtual.set(this.router.url);
    this._subPerfilAtual.set(this.routes.getActiveSubPerfil());
    // #2577: logout leva à rota de login sem recarregar o SPA — a decisão "1× por sessão" tem que
    // valer por SESSÃO AUTENTICADA, senão o próximo usuário deste browser nunca vê o convite.
    if (!this.rotaPermitida() && !this.auth.isAuthenticated()) {
      this.tourOfertaResolvida = false;
      this._oferta.set(null);
      this._capituloRetomada.set(null);
      return;
    }
    // Primeiro NavigationEnd autenticado desta sessão: cobre tanto o F5 com token existente
    // quanto o login interativo sem reload completo (mesma razão de existir do código anterior
    // no app.component). `tourPermitido` fica FORA do "resolvida": um host/rota de PDV/admin
    // não avalia a oferta, mas continua avaliável se o usuário voltar a uma tela do ERP.
    if (!this.tourOfertaResolvida && this.rotaPermitida()
      && isPlatformBrowser(this.platformId) && this.auth.isAuthenticated()) {
      this.tourOfertaResolvida = true;
      void this.avaliarOferta();
    }
  }

  /** Hidrata o estado do tour a partir do banco (login/troca de empresa e F5 com token
   *  existente não passam pelo mesmo fluxo) e só então decide a oferta — decidir antes faria o
   *  motor julgar "novato vs retomar" a partir de um estado ainda não carregado. */
  private async avaliarOferta(): Promise<void> {
    await this.prefs.loadFromDb();
    if (this.conviteSilenciado()) return;
    if (this.motor.elegivel() && !this.motor.jaConcluiu()) {
      const pendente = this.motor.temTourPendente();
      this._capituloRetomada.set(pendente ? this.motor.capituloDoTourPendente() : null);
      this._oferta.set(pendente ? 'retomar' : 'iniciar');
    }
  }

  /** Texto do convite (novato/retomada) — UMA linha curta: o balão do orbe é de linha única (C2/C3). */
  textoConvite(): string {
    if (this._oferta() !== 'retomar') return 'Te mostro o One por partes?';
    const capitulo = this._capituloRetomada();
    return capitulo ? `Continuar o tour em ${capitulo}?` : 'Continuar o tour de onde parou?';
  }

  /** Nunca retoma sozinho: o clique é o gesto que autoriza o autoplay do primeiro áudio. */
  async aceitarOferta(): Promise<void> {
    const modo = this._oferta();
    this._oferta.set(null);
    if (modo === 'retomar') await this.motor.retomar();
    else if (modo === 'iniciar') await this.motor.iniciar();
  }

  /** B2: fechar o convite é RECUSA — conta na empresa e persiste na hora. */
  dispensarOferta(): void {
    this._oferta.set(null);
    this.atualizarGuiaDaEmpresa((e) => ({ ...e, convitesRecusados: e.convitesRecusados + 1 }));
  }

  /** D67 — despacha a ação de um chip. Todas fecham o chat antes (mesmo clicando fora dele,
   *  `fechar()` é idempotente): o tour/replay não deve competir visualmente com o orbe aberto. */
  async executar(id: IdAcaoGuia): Promise<void> {
    this.ai.fechar();
    switch (id) {
      case 'tour-tela': {
        const trecho = this.trechoAtual();
        if (trecho) await this.motor.iniciarTrecho(trecho);
        return;
      }
      case 'continuar':
        await this.motor.retomar();
        return;
      case 'comecar':
        await this.motor.iniciar();
        return;
      case 'refazer-capitulo': {
        const trecho = this.trechoAtual();
        if (!trecho) return;
        const doCapitulo = trechoDoCapitulo(this.roteiro, trecho.capitulo.id, this.temRota);
        if (doCapitulo) await this.motor.iniciarTrecho(doCapitulo);
        return;
      }
    }
  }

  /**
   * D69 — atalho global `?`: abre o orbe (chips visíveis) fora de campo de texto e de rota
   * bloqueada. Devolve o desinstalador; o chamador (overlay, Task 4) chama isto no `ngOnInit`
   * e o devolvido no `ngOnDestroy`. No-op no servidor (retorna um desinstalador vazio) — nunca
   * um `document`/`window` solto fora de `isPlatformBrowser`.
   */
  instalarAtalho(): () => void {
    if (!isPlatformBrowser(this.platformId)) return () => {};
    const aoTeclar = (evento: KeyboardEvent): void => {
      if (evento.key !== '?' || evento.ctrlKey || evento.altKey || evento.metaKey) return;
      const alvo = evento.target as HTMLElement | null;
      const tag = alvo?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (alvo?.closest?.('[contenteditable]')) return;
      if (!this.rotaPermitida()) return;
      evento.preventDefault();
      this.ai.abrir();
    };
    this.doc.addEventListener('keydown', aoTeclar);
    return () => this.doc.removeEventListener('keydown', aoTeclar);
  }
}
